import * as React from "react";
import { Fingerprint, KeyRound, Loader2, QrCode } from "lucide-react";

/**
 * CeremonyPrompt — the "please touch your authenticator"
 * screen shown while a WebAuthn ceremony is in flight.
 * Used for both sign-in (assertion) and enrollment
 * (attestation), differentiated by the `mode` prop.
 *
 * The browser's native authenticator picker is modal on
 * top of this component, so the copy here is effectively
 * the BACKGROUND behind the browser UI. We keep it
 * minimal: an icon, a heading, and a short instruction.
 *
 * The `variant` prop lets the caller pick the illustration
 * that matches the user's hint preference — a fingerprint
 * for `client-device`, a QR code icon for `hybrid`, a
 * generic key icon otherwise.
 */

export type CeremonyPromptMode = "signin" | "enroll";
export type CeremonyPromptVariant = "platform" | "hybrid" | "any";

/**
 * ceremonyVariantFor maps a HintPreference (or bare string —
 * auth-core's PasskeyTransport also lands here) to the
 * matching CeremonyPromptVariant. Kept here alongside the
 * component so every caller (admin, tenant, portal login
 * pages) uses the same mapping and a new preference value
 * only needs to be handled in one place.
 *
 *   other_device  → hybrid (QR illustration)
 *   this_device   → platform (fingerprint)
 *   security_key  → any (generic key)
 *   any other     → any
 */
export function ceremonyVariantFor(preference: string): CeremonyPromptVariant {
  switch (preference) {
    case "other_device":
      return "hybrid";
    case "this_device":
      return "platform";
    default:
      return "any";
  }
}

export interface CeremonyPromptProps {
  mode: CeremonyPromptMode;
  variant?: CeremonyPromptVariant;
}

export function CeremonyPrompt({
  mode,
  variant = "any",
}: CeremonyPromptProps) {
  const { icon, title, hint } = describe(mode, variant);
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      {/* UX-3 (6.4): the pulse animation respects
          `prefers-reduced-motion`. Tailwind's
          `motion-reduce:animate-none` collapses the pulse for
          users who've opted out of motion (vestibular triggers,
          attention sensitivity, battery). The icon itself
          stays visible — we lose only the decorative pulse. */}
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-primary/15 blur-xl animate-pulse motion-reduce:animate-none" />
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
          {icon}
        </div>
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground max-w-xs">{hint}</p>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
        <span>Waiting for your authenticator…</span>
      </div>
    </div>
  );
}

function describe(
  mode: CeremonyPromptMode,
  variant: CeremonyPromptVariant,
): { icon: React.ReactNode; title: string; hint: string } {
  if (mode === "signin") {
    if (variant === "hybrid") {
      return {
        icon: <QrCode className="h-8 w-8" />,
        title: "Scan the QR code with your phone",
        // Set the Bluetooth expectation up front — this is the
        // single most-common point of confusion in cross-device
        // sign-in. Users who don't know the QR pairing also uses
        // Bluetooth see a "couldn't connect" failure ten seconds
        // later with no chance to fix the radio state.
        hint: "Your browser will show a QR code. Scan it with your phone's camera, then approve on your phone. Bluetooth must be on for both devices.",
      };
    }
    if (variant === "platform") {
      return {
        icon: <Fingerprint className="h-8 w-8" />,
        title: "Use your passkey to sign in",
        hint: "Touch your fingerprint sensor, look at your camera, or enter your device PIN.",
      };
    }
    return {
      icon: <KeyRound className="h-8 w-8" />,
      title: "Use your passkey to sign in",
      hint: "Follow the prompt in your browser to confirm it's you.",
    };
  }
  // mode === "enroll"
  if (variant === "hybrid") {
    return {
      icon: <QrCode className="h-8 w-8" />,
      title: "Scan the QR code with your phone",
      hint:
        "Your browser will show a QR code. Scan it with your phone to register a passkey across your devices.",
    };
  }
  if (variant === "platform") {
    return {
      icon: <Fingerprint className="h-8 w-8" />,
      title: "Register this device",
      hint: "Touch your fingerprint sensor, look at your camera, or enter your device PIN to create a passkey.",
    };
  }
  return {
    icon: <KeyRound className="h-8 w-8" />,
    title: "Register a passkey",
    hint: "Follow the prompt in your browser to create a new passkey.",
  };
}
